import React from "react";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useSurveyStore } from "../../../../store/Hooks";
import { UpdateSurveySettingsError } from "../../../store/BackOfficeSurveySettingsStore";
import {
  MIN_SURVEY_NAME_LENGTH,
  PROJECT_SURVEY_NAME_VALIDATIONS,
  useBorder,
} from "@pulse/shared-components";
import { SurveyNameSettings } from "../SurveyNameSettings";
import {
  useCornerRadius,
  useFoundationColorTokens,
  useRadioButtonColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { SurveyRespondentHeaderTextSettings } from "../SurveyRespondentHeaderTextSettings";
import { ChangeLogoSettings } from "../ChangeLogoSettings";
import { useRouteNavigator } from "../../../../../routes/RoutesHelper";
import { ThemingSettings } from "../ThemingSettings";
import { LanguagePreferencesSettings } from "../LanguagePreferencesSettings";
import { FontPreferencesSettings } from "../FontPreferencesSettings";
import { SurveySettingsTabContentHeader } from "../SurveySettingsTabContentHeader";
import { SurveySettingsTab } from "../SurveySettingsPaneTab";

export const SurveySettingsGeneralTab: React.FC = observer(() => {
  const { t } = useTranslation();
  const spacing = useSpacing();
  const typography = useTypography();
  const tokens = useFoundationColorTokens();
  const radioButtonTokens = useRadioButtonColorTokens();
  const border = useBorder();
  const borderRadius = useCornerRadius();
  const navigateTo = useRouteNavigator();
  const surveyStore = useSurveyStore();

  const updatedBackOfficeSurveySettings =
    surveyStore.backOfficeSurveySettingsStore.updatedBackOfficeSurveySettings;

  if (!updatedBackOfficeSurveySettings) return;

  const updatedBackOfficeSurveySettingsUIStore =
    updatedBackOfficeSurveySettings.backOfficeSurveySettingsUIStore;
  const surveySettings = updatedBackOfficeSurveySettings.surveySettings;

  const getSurveyNameTextFieldHelperText = (): string | undefined => {
    switch (surveyStore.backOfficeSurveySettingsStore.rpcError) {
      case UpdateSurveySettingsError.InvalidSurveyName: {
        return t("surveys.surveyNameHelperText.invalidSurveyName", {
          allowedSpecialCharacters:
            PROJECT_SURVEY_NAME_VALIDATIONS.allowedSpecialCharacters,
        });
      }
      case UpdateSurveySettingsError.InvalidSurveyNameLength: {
        return t("surveys.settings.genericHelperText", {
          minCharacterLength: MIN_SURVEY_NAME_LENGTH,
        });
      }
      case UpdateSurveySettingsError.SurveyNameNotUnique: {
        return t("surveys.surveyNameHelperText.surveyNameNotUnique");
      }
    }
  };

  return (
    <Stack id={SurveySettingsTab.General}>
      <SurveySettingsTabContentHeader
        title={t("surveys.settings.generalSectionLabel", "General")}
      />
      <SurveyNameSettings
        surveyName={updatedBackOfficeSurveySettings.surveyName}
        setSurveyName={(surveyName: string) => {
          updatedBackOfficeSurveySettings.setSurveyName(surveyName);
          surveyStore.clearRpcErrors();
          updatedBackOfficeSurveySettingsUIStore.clearError();
        }}
        doesUpdateSurveyNameTextFieldContainError={
          surveyStore.backOfficeSurveySettingsStore
            .doesUpdateSurveyNameTextFieldContainError
        }
        surveyNameHelperText={getSurveyNameTextFieldHelperText()}
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
      />
      <SurveyRespondentHeaderTextSettings
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
        isSurveyRespondentHeaderTextVisible={
          updatedBackOfficeSurveySettings.isSurveyRespondentHeaderTextVisible
        }
        setIsSurveyRespondentHeaderTextVisible={
          updatedBackOfficeSurveySettings.setIsSurveyRespondentHeaderTextVisible
        }
        surveyRespondentHeaderType={
          updatedBackOfficeSurveySettings.surveyRespondentHeaderType
        }
        setSelectedSurveyRespondentHeaderType={
          updatedBackOfficeSurveySettings.setSelectedSurveyRespondentHeaderType
        }
        radioButtonTokens={radioButtonTokens}
        respondentColumns={
          surveyStore.backOfficeSurveySettingsStore.dropdownRespondentColumns
        }
        selectedRespondentColumn={
          updatedBackOfficeSurveySettings.respondentColumnForSurveyRespondentText
        }
        setSelectedRespondentColumn={
          updatedBackOfficeSurveySettings.setSelectedRespondentColumn
        }
        customFallbackTextForSurveyRespondentHeaderText={
          updatedBackOfficeSurveySettings.customFallbackTextForSurveyRespondentHeaderText
        }
        setCustomFallbackTextForSurveyRespondentHeaderText={
          updatedBackOfficeSurveySettings.setCustomFallbackTextForSurveyRespondentHeaderText
        }
        doesCustomFallbackTextInputFieldContainError={
          updatedBackOfficeSurveySettings.backOfficeSurveySettingsUIStore
            .doesCustomFallbackTextInputFieldContainError
        }
        doesRespondentColumnDropdownContainError={
          updatedBackOfficeSurveySettings.backOfficeSurveySettingsUIStore
            .doesRespondentColumnDropdownContainError
        }
      />
      {updatedBackOfficeSurveySettings &&
        updatedBackOfficeSurveySettings.surveySettings.themedImage && (
          <ChangeLogoSettings
            t={t}
            spacing={spacing}
            typography={typography}
            tokens={tokens}
            border={border}
            lightThemeLogoUrl={
              updatedBackOfficeSurveySettings.surveySettings.themedImage
                ?.lightImageURL
            }
            darkThemeLogoUrl={
              updatedBackOfficeSurveySettings.surveySettings.themedImage
                ?.darkImageURL
            }
            cornerRadius={borderRadius}
            navigateTo={navigateTo}
            backOfficeSurveySettingsStore={
              surveyStore.backOfficeSurveySettingsStore
            }
            updateClientDarkThemeLogoURL={
              updatedBackOfficeSurveySettings.surveySettings.themedImage
                .setDarkImageURL
            }
            updateClientLightThemeLogoURL={
              updatedBackOfficeSurveySettings.surveySettings.themedImage
                .setLightImageURL
            }
            updateClientLightThemeLogoId={
              updatedBackOfficeSurveySettings.setLightThemeLogoId
            }
            updateClientDarkThemeLogoId={
              updatedBackOfficeSurveySettings.setDarkThemeLogoId
            }
            setIsErrorBannerVisible={
              updatedBackOfficeSurveySettings.backOfficeSurveySettingsUIStore
                .setIsErrorSnackbarVisible
            }
          />
        )}
      <ThemingSettings
        setSelectedSurveyTheme={
          updatedBackOfficeSurveySettings.setSelectedSurveyTheme
        }
        selectedSurveyTheme={
          updatedBackOfficeSurveySettings.selectedSurveyTheme
        }
        supportedThemes={
          surveyStore.backOfficeSurveySettingsStore.supportedThemes
        }
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
        borderRadius={borderRadius}
      />
      <LanguagePreferencesSettings
        setSelectedLanguageCode={
          updatedBackOfficeSurveySettings.setSelectedLanguageCode
        }
        selectedLanguageId={surveySettings.selectedLanguageId}
        languageList={surveySettings.languageList}
        isLanguageSelectionOptionPersistent={
          surveySettings.isLanguageSelectionOptionPersistent
        }
        isLanguageSelectionDisplayedOnStart={
          surveySettings.isLanguageSelectionDisplayedOnStart
        }
        setIsLanguageSelectionOptionPersistent={
          updatedBackOfficeSurveySettings.setIsLanguageSelectionOptionPersistent
        }
        setIsLanguageSelectionDisplayedOnStart={
          updatedBackOfficeSurveySettings.setIsLanguageSelectionDisplayedOnStart
        }
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
      />
      <FontPreferencesSettings
        selectedFont={surveySettings.font}
        fontsList={surveyStore.backOfficeSurveySettingsStore.supportedFonts}
        setSelectedFont={updatedBackOfficeSurveySettings.setSelectedFont}
        selectedFontSize={surveySettings.fontSizeScale}
        setSelectedFontSize={
          updatedBackOfficeSurveySettings.setSelectedFontSize
        }
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
      />
    </Stack>
  );
});
